import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FinMark from '../components/brand/FinMark';
import { richText } from '../components/landing/richText';
import { landing } from '../components/landing/tokens';
import { CONTACT_EMAIL, LegalDocument } from '../legal/legalDocument';

interface Props {
  document: LegalDocument;
  /** The other legal page, linked at the foot. */
  other: { to: string; label: string };
}

/**
 * The privacy policy or the terms of use: public, readable signed in or out,
 * in the landing's look. The text is Portuguese for every visitor; others
 * get a line saying so.
 */
const LegalPage: React.FC<Props> = ({ document: legal, other }) => {
  const { t } = useTranslation('landing');
  const onlyPortuguese = t('legal.onlyPortuguese');

  useEffect(() => {
    const previous = window.document.title;
    window.document.title = `${legal.title} · Financy`;
    window.scrollTo?.(0, 0);
    return () => {
      window.document.title = previous;
    };
  }, [legal.title]);

  const linkSx = { color: landing.accent, fontWeight: 600 };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: landing.bg, color: landing.ink, fontFamily: landing.body, fontSize: 16, lineHeight: 1.65 }}>
      <Box component='header' sx={{ borderBottom: `1px solid ${landing.line}` }}>
        <Box sx={{ maxWidth: 760, mx: 'auto', px: 2, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box component={RouterLink} to='/' sx={{ display: 'flex', alignItems: 'center', gap: 1, color: landing.ink, textDecoration: 'none', fontFamily: landing.display, fontWeight: 700, fontSize: 18 }}>
            <FinMark size={28} />
            Financy
          </Box>
          <Box component={RouterLink} to='/' sx={{ ...linkSx, fontSize: 14 }}>
            {t('legal.back')}
          </Box>
        </Box>
      </Box>

      <Box component='main' sx={{ maxWidth: 760, mx: 'auto', px: 2, py: { xs: 4, md: 6 }, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box component='h1' sx={{ m: 0, fontFamily: landing.display, fontSize: { xs: 32, md: 42 }, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
            {legal.title}
          </Box>
          <Box component='p' sx={{ m: 0, color: landing.ink3, fontSize: 14 }}>
            Última atualização: {legal.updated}
          </Box>
          {onlyPortuguese && (
            <Box component='p' sx={{ m: 0, color: landing.ink3, fontSize: 14 }}>
              {onlyPortuguese}
            </Box>
          )}
        </Box>

        <Box lang='pt-BR' sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {legal.intro.map((paragraph) => (
            <Box key={paragraph} component='p' sx={{ m: 0, fontSize: 17 }}>
              {richText(paragraph)}
            </Box>
          ))}

          {legal.sections.map((section, index) => (
            <Box key={section.title} component='section' sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box component='h2' sx={{ m: 0, fontFamily: landing.display, fontSize: 21, lineHeight: 1.3 }}>
                {index + 1}. {section.title}
              </Box>
              {section.paragraphs?.map((paragraph) => (
                <Box key={paragraph} component='p' sx={{ m: 0, color: landing.ink2 }}>
                  {richText(paragraph)}
                </Box>
              ))}
              {section.items && (
                <Box component='ul' sx={{ m: 0, pl: 3, display: 'flex', flexDirection: 'column', gap: 0.75, color: landing.ink2 }}>
                  {section.items.map((item) => (
                    <li key={item}>{richText(item)}</li>
                  ))}
                </Box>
              )}
              {section.after?.map((paragraph) => (
                <Box key={paragraph} component='p' sx={{ m: 0, color: landing.ink2 }}>
                  {richText(paragraph)}
                </Box>
              ))}
            </Box>
          ))}
        </Box>

        <Box component='footer' sx={{ mt: 2, pt: 3, borderTop: `1px solid ${landing.line}`, display: 'flex', flexWrap: 'wrap', gap: 3, fontSize: 14 }}>
          <Box component={RouterLink} to={other.to} sx={linkSx}>
            {other.label}
          </Box>
          <Box component='a' href={`mailto:${CONTACT_EMAIL}`} sx={linkSx}>
            {CONTACT_EMAIL}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LegalPage;
